import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/config';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  CreditCard, Check, Clock, Crown, AlertTriangle, 
  Loader2, CheckCircle, XCircle, ArrowRight
} from 'lucide-react';

const SubscriptionPage = () => {
  const { user, subscription, refreshSubscription, api, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check if returning from successful payment
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/subscription/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await api.get(`/subscription/checkout-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast.success('Betaling succesvol! Je subscription is nu actief.');
          await refreshSubscription();
          // Remove session_id from URL
          navigate('/company/subscription', { replace: true });
          setCheckingPayment(false);
          return;
        }
        
        if (response.data.status === 'expired') {
          toast.error('Betalingssessie verlopen. Probeer opnieuw.');
          navigate('/company/subscription', { replace: true });
          setCheckingPayment(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Betaling wordt verwerkt. Check later je status.');
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleSubscribe = async () => {
    if (!config?.stripe_configured) {
      toast.error('Stripe is niet geconfigureerd. Neem contact op met de beheerder.');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/subscription/create-checkout', { origin_url: originUrl });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Fout bij starten betaling');
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Weet je zeker dat je je subscription wilt opzeggen? Je houdt toegang tot het einde van de huidige periode.')) {
      return;
    }

    try {
      await api.post('/subscription/cancel');
      toast.success('Subscription wordt beëindigd aan het einde van de huidige periode');
      await refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij opzeggen');
    }
  };

  const getStatusBadge = () => {
    if (!subscription) return null;
    
    switch (subscription.status) {
      case 'admin':
        return <Badge className="bg-purple-600">Admin</Badge>;
      case 'active':
        return <Badge className="bg-green-600">Actief</Badge>;
      case 'trial':
        return <Badge className="bg-blue-600">Proefperiode</Badge>;
      case 'gifted':
        return <Badge className="bg-yellow-600">Cadeau</Badge>;
      case 'cancelled':
        return <Badge className="bg-orange-600">Opgezegd</Badge>;
      case 'expired':
        return <Badge className="bg-red-600">Verlopen</Badge>;
      default:
        return <Badge variant="outline">Geen subscription</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-orange-600 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Betaling wordt verwerkt...</h2>
            <p className="text-gray-600">Even geduld alsjeblieft</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Crown className="w-16 h-16 mx-auto text-orange-600 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription</h1>
          <p className="text-gray-600">Beheer je Horeca Dashboard subscription</p>
        </div>

        {/* Current Status */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Huidige Status</CardTitle>
              {getStatusBadge()}
            </div>
          </CardHeader>
          <CardContent>
            {subscription?.status === 'admin' ? (
              <div className="flex items-center gap-3 text-purple-700 bg-purple-50 p-4 rounded-lg">
                <Crown className="w-6 h-6" />
                <div>
                  <p className="font-medium">Admin Account</p>
                  <p className="text-sm">Je hebt volledige toegang als administrator.</p>
                </div>
              </div>
            ) : subscription?.has_access ? (
              <div className="space-y-4">
                {subscription.status === 'trial' && (
                  <div className="flex items-center gap-3 text-blue-700 bg-blue-50 p-4 rounded-lg">
                    <Clock className="w-6 h-6" />
                    <div>
                      <p className="font-medium">Proefperiode</p>
                      <p className="text-sm">
                        Je proefperiode eindigt op {formatDate(subscription.trial_end)}
                      </p>
                    </div>
                  </div>
                )}
                
                {subscription.status === 'active' && (
                  <div className="flex items-center gap-3 text-green-700 bg-green-50 p-4 rounded-lg">
                    <CheckCircle className="w-6 h-6" />
                    <div>
                      <p className="font-medium">Actieve Subscription</p>
                      <p className="text-sm">
                        Volgende factuurdatum: {formatDate(subscription.subscription_end)}
                      </p>
                    </div>
                  </div>
                )}

                {subscription.status === 'gifted' && (
                  <div className="flex items-center gap-3 text-yellow-700 bg-yellow-50 p-4 rounded-lg">
                    <Crown className="w-6 h-6" />
                    <div>
                      <p className="font-medium">Cadeau Subscription</p>
                      <p className="text-sm">
                        Geldig tot {formatDate(subscription.subscription_end)} ({subscription.gifted_months} maanden)
                      </p>
                    </div>
                  </div>
                )}

                {subscription.status === 'cancelled' && (
                  <div className="flex items-center gap-3 text-orange-700 bg-orange-50 p-4 rounded-lg">
                    <AlertTriangle className="w-6 h-6" />
                    <div>
                      <p className="font-medium">Subscription Opgezegd</p>
                      <p className="text-sm">
                        Je hebt nog toegang tot {formatDate(subscription.subscription_end)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 text-red-700 bg-red-50 p-4 rounded-lg">
                <XCircle className="w-6 h-6" />
                <div>
                  <p className="font-medium">Geen Actieve Subscription</p>
                  <p className="text-sm">
                    {subscription?.status === 'expired' 
                      ? 'Je subscription is verlopen. Neem een nieuwe subscription om door te gaan.'
                      : 'Start een subscription om toegang te krijgen tot alle functies.'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Plan */}
        {(subscription?.status !== 'admin' && (
          !subscription?.has_access || 
          subscription?.status === 'trial' ||
          subscription?.status === 'cancelled'
        )) && (
          <Card className="mb-8 border-2 border-orange-200">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-6 h-6" />
                Horeca Dashboard Pro
              </CardTitle>
              <CardDescription className="text-orange-100">
                Alles wat je nodig hebt voor je horecazaak
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-4">Wat krijg je:</h3>
                  <ul className="space-y-3">
                    {[
                      'Volledig Dashboard Overzicht',
                      'Omzet & Verkoop Tracking',
                      'Kosten & Marges Beheer',
                      'Voorraad & Inkoop',
                      'Personeel & Planning',
                      'Marketing & Acties',
                      'Reserveringen Systeem',
                      'KPI Management',
                      'AI-Powered Inzichten',
                      'Unlimited Exports'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex flex-col justify-center items-center bg-gray-50 rounded-lg p-6">
                  {config?.stripe_configured ? (
                    <>
                      <p className="text-sm text-gray-600 mb-2">
                        {subscription?.status === 'trial' 
                          ? 'Na je proefperiode:'
                          : 'Start vandaag met:'}
                      </p>
                      <p className="text-gray-500 mb-6">
                        {config?.trial_days} dagen gratis proefperiode
                      </p>
                      <Button 
                        size="lg" 
                        className="w-full bg-orange-600 hover:bg-orange-700"
                        onClick={handleSubscribe}
                        disabled={loading}
                        data-testid="subscribe-btn"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <CreditCard className="w-5 h-5 mr-2" />
                        )}
                        {subscription?.status === 'trial' ? 'Nu Upgraden' : 'Start Subscription'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </>
                  ) : (
                    <div className="text-center text-gray-500">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                      <p className="font-medium">Betalingen niet beschikbaar</p>
                      <p className="text-sm mt-2">
                        Stripe is niet geconfigureerd in deze omgeving.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cancel Subscription */}
        {subscription?.status === 'active' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-600">Subscription Beheer</CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleCancelSubscription}
                data-testid="cancel-subscription-btn"
              >
                Subscription Opzeggen
              </Button>
              <p className="text-sm text-gray-500 mt-2">
                Je houdt toegang tot het einde van je huidige betaalperiode.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Back to Dashboard or Logout */}
        <div className="mt-8 text-center space-x-4">
          {subscription?.has_access && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/company/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              Terug naar Dashboard
            </Button>
          )}
          <Button 
            variant="ghost" 
            className="text-gray-500"
            onClick={logout}
            data-testid="logout-btn"
          >
            Uitloggen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
